import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Breadcrumbs } from './Breadcrumbs';
import { EditIcon, PlusIcon, XIcon, DownloadIcon, ChevronDownIcon, EyeIcon } from './Icons';
import type { Filter, FilterableField } from '../types';
import { useAuth } from '../auth/AuthContext';

export const DetailField = ({ label, value, isEditing = false, name, onChange, type = 'text', options }: { label: string, value: string | number | null | undefined, isEditing?: boolean, name?: string, onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void, type?: string, options?: {value: string, label: string}[] }) => {
    const renderInput = () => {
        if (type === 'select') {
             return <select className="form-input" name={name} value={String(value) ?? ''} onChange={onChange}>{options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
        }
        return <input className="form-input" type={type} name={name} value={String(value) ?? ''} onChange={onChange} />;
    };
    return (
         <div className="detail-item">
            <label className="detail-label">{label}</label>
            {isEditing ? renderInput() : <span className="detail-value">{value ?? 'N/A'}</span>}
        </div>
    );
};


const FilterEditor = ({ onApply, onClose, filterToEdit, style, filterableFields }: { onApply: (filter: Filter) => void; onClose: () => void; filterToEdit: Filter | null; style: React.CSSProperties; filterableFields: FilterableField[] }) => {
    const [filter, setFilter] = useState<Filter>(filterToEdit || { id: Date.now().toString(), field: filterableFields[0].value, value: '' });
    const valueInputRef = useRef<HTMLInputElement>(null);
    
    const sqlKeywords = /\b(ALTER|CREATE|DELETE|DROP|EXEC|EXECUTE|INSERT|MERGE|SELECT|TRUNCATE|UPDATE|UNION)\b/gi;

    useEffect(() => {
        setFilter(filterToEdit || { id: Date.now().toString(), field: filterableFields[0].value, value: '' });
        setTimeout(() => valueInputRef.current?.focus(), 0);
    }, [filterToEdit, filterableFields]);

    const handleApply = () => {
        let sanitizedValue = filter.value.trim().replace(/<[^>]*>?/gm, '');
        sanitizedValue = sanitizedValue.replace(sqlKeywords, '');
        if(sanitizedValue){
            onApply({ ...filter, value: sanitizedValue });
        }
    };

    const handleFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setFilter(prevFilter => ({ ...prevFilter, field: e.target.value, value: '' }));
        valueInputRef.current?.focus();
    };
    
    return (
        <div className="filter-popover" style={style}>
            <h3 className="modal-title">{filterToEdit ? 'Editar Filtro' : 'Adicionar Filtro'}</h3>
            <div className="filter-popover-grid">
                <div>
                    <label htmlFor="filter-field">Campo</label>
                    <select id="filter-field" value={filter.field} className="form-input" onChange={handleFieldChange}>
                        {filterableFields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="filter-value">Valor</label>
                    <input ref={valueInputRef} id="filter-value" type="text" value={filter.value} className="form-input" onChange={(e) => setFilter({ ...filter, value: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && handleApply()}/>
                </div>
            </div>
            <div className="btn-group">
                <button className="btn btn-danger" onClick={onClose}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleApply}>{filterToEdit ? 'Salvar' : 'Adicionar'}</button>
            </div>
        </div>
    );
};

type ExportFormat = 'csv' | 'json' | 'xml' | 'txt';

const ExportMenu = ({ onExport }: { onExport: (format: ExportFormat) => void }) => {
    return (
        <div className="dropdown-menu">
            <button onClick={() => onExport('csv')}>Exportar como CSV</button>
            <button onClick={() => onExport('json')}>Exportar como JSON</button>
            <button onClick={() => onExport('xml')}>Exportar como XML</button>
            <button onClick={() => onExport('txt')}>Exportar como TXT</button>
        </div>
    );
};

export interface TableColumn<T> {
    header: string;
    key: string;
    render: (item: T) => React.ReactNode;
    exportValue?: (item: T) => string | number | null;
    cellClassName?: string;
}

interface CadastroTemplateProps<T extends { id: number | string }> {
    currentPage: string;
    pageTitle: string;
    entityName: string;
    tableName: string;
    filterableFields: FilterableField[];
    tableColumns: TableColumn<T>[];
    fetchData: () => Promise<T[]>;
    updateData?: (data: T) => Promise<boolean>;
    ModalComponent: React.FC<{
        data: T | null;
        onClose: () => void;
        isEditing: boolean;
        onSave: (data: T) => void;
    }>;
}

export function CadastroTemplate<T extends { id: number | string }>({ currentPage, pageTitle, entityName, tableName, filterableFields, tableColumns, fetchData, updateData, ModalComponent }: CadastroTemplateProps<T>) {
  const [allData, setAllData] = useState<T[]>([]);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<Filter[]>([]);
  const [isFilterEditorOpen, setFilterEditorOpen] = useState(false);
  const [filterToEdit, setFilterToEdit] = useState<Filter | null>(null);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isExportMenuOpen, setExportMenuOpen] = useState(false);
  const filterBuilderRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const { hasPermission } = useAuth();

  const canUpdate = useMemo(() => hasPermission(tableName, 'Update'), [hasPermission, tableName]);
  const canInsert = useMemo(() => hasPermission(tableName, 'Insert'), [hasPermission, tableName]);

  const openFilterEditor = useCallback((filter: Filter | null, anchorEl?: HTMLElement) => {
    if (anchorEl && filterBuilderRef.current) {
        const anchorRect = anchorEl.getBoundingClientRect();
        const containerRect = filterBuilderRef.current.getBoundingClientRect();
        setPopoverStyle({
            position: 'absolute',
            top: `${anchorRect.bottom - containerRect.top + 8}px`,
            left: `${anchorRect.left - containerRect.left}px`,
            right: 'auto',
        });
    } else {
        setPopoverStyle({});
    }
    setFilterToEdit(filter);
    setFilterEditorOpen(true);
  }, []);
  
  const closeFilterEditor = () => {
    setFilterToEdit(null);
    setFilterEditorOpen(false);
  };

  const handleApplyFilter = (newFilter: Filter) => {
    const existingIndex = filters.findIndex(f => f.id === newFilter.id);
    if(existingIndex > -1){
        setFilters(filters.map(f => f.id === newFilter.id ? newFilter : f));
    } else {
        setFilters([...filters, newFilter]);
    }
    closeFilterEditor();
  };

  const handleRemoveFilter = (id: string) => {
    setFilters(filters.filter(f => f.id !== id));
  };
  
  const handleClearFilters = () => {
    setFilters([]);
    setAppliedFilters([]);
    setHasSearched(false);
    setCurrentPageNum(1);
    setAllData([]);
  };

  const performSearch = useCallback(async () => {
    setIsLoading(true);
    setHasSearched(true);
    setCurrentPageNum(1);
    try {
        const data = await fetchData();
        setAllData(data);
        setAppliedFilters(filters);
    } catch (error) {
        console.error("Failed to fetch data:", error);
        alert((error as Error).message);
    } finally {
        setIsLoading(false);
    }
  }, [filters, fetchData]);

  const filteredData = useMemo(() => {
    if (!hasSearched) return [];
    if (appliedFilters.length === 0) return allData;
    
    return allData.filter(item => appliedFilters.every(filter => {
        const itemValue = (item as any)[filter.field];
        if (itemValue === null || itemValue === undefined) return false;
        
        let comparableValue = itemValue;
        if (typeof comparableValue === 'boolean' || (typeof comparableValue === 'number' && (comparableValue === 0 || comparableValue === 1))) {
            comparableValue = comparableValue ? 'sim' : 'não';
        }
        
        return String(comparableValue).toLowerCase().includes(filter.value.toLowerCase());
    }));
  }, [appliedFilters, allData, hasSearched]);
  
  const paginatedData = useMemo(() => {
    if (rowsPerPage === Infinity) {
        return filteredData;
    }
    const startIndex = (currentPageNum - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPageNum, rowsPerPage]);

  const totalPages = useMemo(() => {
    if(rowsPerPage === Infinity) return 1;
    return Math.ceil(filteredData.length / rowsPerPage);
  }, [filteredData, rowsPerPage]);


  const handleSave = async (data: T) => {
      if(updateData){
        const success = await updateData(data);
        if(success) {
            setAllData(prev => prev.map(d => d.id === data.id ? data : d));
        }
      } else {
        setAllData(prev => prev.map(d => d.id === data.id ? data : d));
      }
      setSelectedItem(null);
      setIsEditMode(false);
  };

  const openModal = (item: T, editMode: boolean) => {
    setSelectedItem(item);
    setIsEditMode(editMode && canUpdate);
  };

  const handleExport = (format: ExportFormat) => {
    if (filteredData.length === 0) return;

    const getExportFileName = (entityName: string, format: ExportFormat) => {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const domain = entityName.toLowerCase().replace(/\s/g, '_');
        return `${domain}_${day}${month}${year}_${hours}${minutes}.${format}`;
    };

    const fileName = getExportFileName(entityName, format);

    const downloadFile = (content: string, fileName: string, mimeType: string) => {
        const blob = new Blob([content], { type: mimeType });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getFormattedValue = (item: T, col: TableColumn<T>) => {
        let value = col.exportValue ? col.exportValue(item) : (item as any)[col.key];
        return value;
    };
    
    const dataToExport = filteredData.map(item => {
        const row: { [key: string]: any } = {};
        tableColumns.forEach(col => {
            row[col.header] = getFormattedValue(item, col) ?? '';
        });
        return row;
    });

    if (format === 'csv') {
        const headers = tableColumns.map(col => `"${col.header}"`);
        const rows = filteredData.map(item =>
            tableColumns.map(col => {
                const value = getFormattedValue(item, col);
                const stringValue = String(value ?? '').replace(/"/g, '""');
                return `"${stringValue}"`;
            }).join(',')
        );
        const csvContent = [headers.join(','), ...rows].join('\n');
        downloadFile(`\uFEFF${csvContent}`, fileName, 'text/csv;charset=utf-8;');
    } else if (format === 'json') {
        const jsonContent = JSON.stringify(dataToExport, null, 2);
        downloadFile(jsonContent, fileName, 'application/json');
    } else if (format === 'xml') {
        const toXML = (obj: any) => {
            let xml = '';
            for (const prop in obj) {
                const safeKey = prop.replace(/[^a-zA-Z0-9_]/g, '').replace(/^\d/, '_$&');
                if (Array.isArray(obj[prop])) {
                    obj[prop].forEach((item: any) => {
                        xml += `<${safeKey}>${toXML(item)}</${safeKey}>`;
                    });
                } else {
                    const value = obj[prop] === null ? '' : String(obj[prop]).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
                    xml += `<${safeKey}>${value}</${safeKey}>`;
                }
            }
            return xml;
        };
        const xmlContent = `<?xml version="1.0" encoding="UTF-8" ?>\n<data>\n${dataToExport.map(item => `  <item>\n${toXML(item)}\n  </item>`).join('\n')}\n</data>`;
        downloadFile(xmlContent, fileName, 'application/xml');
    } else if (format === 'txt') {
        const headers = tableColumns.map(col => col.header);
        const rows = filteredData.map(item =>
            tableColumns.map(col => {
                const value = getFormattedValue(item, col);
                return String(value ?? '').replace(/\t/g, ' ').replace(/\n|\r/g, ' ');
            }).join('\t')
        );
        const txtContent = [headers.join('\t'), ...rows].join('\n');
        downloadFile(txtContent, fileName, 'text/plain;charset=utf-8;');
    }

    setExportMenuOpen(false);
  };


  useEffect(() => {
    const handleEscAndClickOutside = (event: MouseEvent | KeyboardEvent) => {
       if ('key' in event && event.key === 'Escape') {
            if(selectedItem && !isEditMode) setSelectedItem(null);
            if(isFilterEditorOpen) closeFilterEditor();
            if(isExportMenuOpen) setExportMenuOpen(false);
       }
       if (event.type === 'mousedown') {
            const target = event.target as HTMLElement;
            if (isExportMenuOpen && exportButtonRef.current && !exportButtonRef.current.contains(target) && !target.closest('.dropdown-menu')) {
                setExportMenuOpen(false);
            }
       }
    };
    window.addEventListener('keydown', handleEscAndClickOutside);
    document.addEventListener('mousedown', handleEscAndClickOutside);
    return () => {
        window.removeEventListener('keydown', handleEscAndClickOutside);
        document.removeEventListener('mousedown', handleEscAndClickOutside);
    }
  }, [isFilterEditorOpen, selectedItem, isEditMode, isExportMenuOpen]);

  const actionsColumn: TableColumn<T> = {
    header: 'Ações',
    key: 'actions',
    cellClassName: 'actions-cell',
    render: (item: T) => {
        const handleActionClick = (e: React.MouseEvent, editMode: boolean) => {
            e.stopPropagation();
            openModal(item, editMode);
        };
        
        return canUpdate ? (
            <button className="btn-icon" aria-label={`Editar ${entityName}`} onClick={(e) => handleActionClick(e, true)}>
                <EditIcon />
            </button>
        ) : (
             <button className="btn-icon" aria-label={`Visualizar ${entityName}`} onClick={(e) => handleActionClick(e, false)}>
                <EyeIcon />
            </button>
        );
    },
    exportValue: () => null
  };

  const finalTableColumns = [...tableColumns, actionsColumn];

  return (
    <div>
      <Breadcrumbs currentPage={currentPage} />
      <header className="page-header"><h2 className="page-title">{pageTitle}</h2></header>
      
      <div className="controls-wrapper">
        <div className="actions-and-filters-container" ref={filterBuilderRef}>
            <div className="action-buttons-group">
                <button className="btn btn-primary" onClick={(e) => openFilterEditor(null, e.currentTarget)}><PlusIcon /> Adicionar Filtro</button>
                <button className="btn btn-primary" onClick={performSearch} disabled={filters.length === 0}>Pesquisar</button>
                <button className="btn btn-danger" onClick={handleClearFilters} disabled={filters.length === 0 && !hasSearched}>Limpar Pesquisa</button>
                {canInsert && <button className="btn btn-primary"><PlusIcon />Novo {entityName}</button>}
                <div className="dropdown-container">
                    <button ref={exportButtonRef} className="btn btn-secondary" onClick={() => setExportMenuOpen(prev => !prev)} disabled={filteredData.length === 0}>
                        <DownloadIcon /> Baixar Registros <ChevronDownIcon isOpen={isExportMenuOpen}/>
                    </button>
                    {isExportMenuOpen && <ExportMenu onExport={handleExport} />}
                </div>
            </div>

            {filters.length > 0 && (
                 <div className="filter-bar">
                    {filters.map((filter) => (
                        <div key={filter.id} className="filter-tag" onClick={(e) => openFilterEditor(filter, e.currentTarget)} tabIndex={0} onKeyDown={e => e.key === 'Enter' && openFilterEditor(filter, e.currentTarget)}>
                            <span className="filter-tag-content">
                                <span className="field">{filterableFields.find(f => f.value === filter.field)?.label}:</span>
                                <span className="value">{filter.value}</span>
                            </span>
                            <button className="filter-tag-remove" onClick={(e) => { e.stopPropagation(); handleRemoveFilter(filter.id); }} aria-label={`Remover filtro ${filter.field}`}><XIcon /></button>
                        </div>
                    ))}
                </div>
            )}
            {isFilterEditorOpen && <FilterEditor onApply={handleApplyFilter} onClose={closeFilterEditor} filterToEdit={filterToEdit} style={popoverStyle} filterableFields={filterableFields} />}
        </div>
        {filters.length === 0 && <div className="filter-bar-placeholder">Use o botão "Adicionar Filtro" para refinar sua busca...</div>}
      </div>

      <div className="table-wrapper">
        {isLoading ? (<p style={{textAlign: 'center', padding: '2rem'}}>Carregando...</p>) : !hasSearched ? (
             <div className="table-placeholder">Use o construtor de filtros para encontrar registros.</div>
        ) : filteredData.length === 0 ? (
            <div className="table-placeholder">Nenhum registro encontrado com os filtros aplicados.</div>
        ) : (
            <table className="data-table">
            <thead><tr>{finalTableColumns.map(col => <th key={col.key}>{col.header}</th>)}</tr></thead>
            <tbody>{paginatedData.map((item) => (
                <tr key={item.id} onClick={() => openModal(item, false)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && openModal(item, false)}>
                    {finalTableColumns.map(col => <td key={`${item.id}-${col.key}`} className={col.cellClassName}>{col.render(item)}</td>)}
                </tr>))}
            </tbody>
            </table>
        )}
      </div>

       {hasSearched && filteredData.length > 0 && (
            <div className="pagination-controls">
                <div className="pagination-rows-selector">
                    <label htmlFor="rows-per-page">Itens por página:</label>
                    <select id="rows-per-page" value={rowsPerPage} onChange={e => {setRowsPerPage(Number(e.target.value)); setCurrentPageNum(1);}}>
                        <option value="10">10</option>
                        <option value={Infinity}>Infinito</option>
                    </select>
                </div>
                {rowsPerPage !== Infinity && totalPages > 1 && (
                    <div className="pagination-nav">
                        <button onClick={() => setCurrentPageNum(p => Math.max(1, p - 1))} disabled={currentPageNum === 1}>Anterior</button>
                        <span>Página {currentPageNum} de {totalPages}</span>
                        <button onClick={() => setCurrentPageNum(p => Math.min(totalPages, p + 1))} disabled={currentPageNum === totalPages}>Próxima</button>
                    </div>
                )}
                <div className="pagination-summary">
                    <span>Exibindo {paginatedData.length} de {filteredData.length} registros</span>
                </div>
            </div>
       )}

      {selectedItem && <ModalComponent data={selectedItem} onClose={() => { setSelectedItem(null); setIsEditMode(false); }} isEditing={isEditMode} onSave={handleSave} />}
    </div>
  );
}